import Head from 'next/head'
import { useRouter } from 'next/router'
import { FC, useEffect } from 'react'

import { stringifyJsonLd } from '@/lib/json-ld'
import { useBranding, useBrandingHostname } from '@/hooks/use-branding'
import { useTenantMediaDefaults } from '@/hooks/use-tenant-media-defaults'

import { useNotification } from '@/contexts/notification-context'
import tabNotificationService from '@/services/tab-notification.service'
import {
  getAbsoluteUrl,
  getCleanTitle,
  getSocialShareDomain,
  optimizeTitleForSocial,
  resolveSeoOgImageUrl,
  validateSeoMetadata,
} from '@/utils/seo'

import { buildArticleStructuredData, getSeoContentType } from './seo-metadata-utils'

interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  ogImage?: string
  ogType?: 'website' | 'article' | 'video.other' | 'profile'
  twitterCard?: 'summary' | 'summary_large_image' | 'player'
  canonicalUrl?: string
  author?: string
  publishedTime?: string
  modifiedTime?: string
  section?: string
  tags?: string[]
  siteName?: string
  locale?: string
  videoUrl?: string
  videoDuration?: string
  videoWidth?: number
  videoHeight?: number
  /** When false, og:title is the page title only (no "| SiteName" suffix). */
  appendBrandToSocialTitle?: boolean
  /**
   * Overrides twitter:description. Use `null` to omit the tag (X falls back to og:description).
   * Use `''` to render an empty description and avoid a duplicate domain line on X.
   */
  twitterDescription?: string | null
  noIndex?: boolean
}

const SEO: FC<SEOProps> = ({
  title,
  description,
  keywords,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  canonicalUrl,
  author,
  publishedTime,
  modifiedTime,
  section,
  tags = [],
  siteName,
  locale,
  videoUrl,
  videoDuration,
  videoWidth,
  videoHeight,
  appendBrandToSocialTitle = true,
  twitterDescription,
  noIndex = false,
}) => {
  const router = useRouter()
  const { unreadCount } = useNotification()
  const branding = useBranding()
  const hostname = useBrandingHostname()
  const { defaultThumbnailUrl, isFsdsDomain } = useTenantMediaDefaults()

  const validatedMetadata = validateSeoMetadata(
    {
      title,
      description,
      author: author || branding.publisherName,
      keywords,
    },
    {
      defaultTitle: branding.siteName,
      defaultDescription: branding.seoMeta.defaultDescription,
      defaultAuthor: branding.publisherName,
      defaultKeywords: branding.seoMeta.defaultKeywords,
    }
  )

  const socialTitle = appendBrandToSocialTitle
    ? optimizeTitleForSocial(validatedMetadata.title, branding.siteName)
    : validatedMetadata.title
  const cleanTitle = getCleanTitle(validatedMetadata.title, branding.siteName)
  const socialShareDomain = getSocialShareDomain(hostname)
  const effectiveSiteName = siteName || socialShareDomain || branding.siteName
  const effectiveLocale = locale || branding.seoMeta.locale || 'vi_VN'
  const resolvedTwitterDescription =
    twitterDescription === undefined ? validatedMetadata.description : twitterDescription

  const pageCanonicalUrl = canonicalUrl
    ? getAbsoluteUrl(canonicalUrl, hostname)
    : getAbsoluteUrl(router.asPath, hostname)
  const resolvedOgImage = resolveSeoOgImageUrl(ogImage, {
    isFsdsDomain,
    defaultThumbnailUrl,
  })
  const pageOgImage = getAbsoluteUrl(resolvedOgImage, hostname)

  useEffect(() => {
    if (validatedMetadata.title) {
      tabNotificationService['originalTitle'] = validatedMetadata.title
    }
  }, [validatedMetadata.title])

  const socialContentType = getSeoContentType(router.pathname, ogType, Boolean(videoUrl))

  const getTitle = () => {
    if (unreadCount > 0) {
      return `(${unreadCount}) ${validatedMetadata.title}`
    }
    return validatedMetadata.title
  }

  return (
    <Head>
      {/* Basic Meta Tags - These are critical for SSR */}
      <title key="title">{getTitle()}</title>
      <meta key="description" name="description" content={validatedMetadata.description} />
      <meta key="keywords" name="keywords" content={validatedMetadata.keywords} />
      <meta key="author" name="author" content={validatedMetadata.author} />
      <meta key="robots" name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />
      <meta key="googlebot" name="googlebot" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />

      {/* Open Graph Tags - Facebook & LinkedIn */}
      <meta key="og:title" property="og:title" content={socialTitle} />
      <meta key="og:description" property="og:description" content={validatedMetadata.description} />
      <meta key="og:type" property="og:type" content={socialContentType} />
      <meta key="og:image" property="og:image" content={pageOgImage} />
      <meta key="og:image:width" property="og:image:width" content="1200" />
      <meta key="og:image:height" property="og:image:height" content="630" />
      <meta key="og:image:alt" property="og:image:alt" content={`${cleanTitle} - ${validatedMetadata.description}`} />
      <meta key="og:url" property="og:url" content={pageCanonicalUrl} />
      <meta key="og:site_name" property="og:site_name" content={effectiveSiteName} />
      <meta key="og:locale" property="og:locale" content={effectiveLocale} />

      {/* Article specific Open Graph tags */}
      {socialContentType === 'article' && (
        <>
          {validatedMetadata.author && (
            <meta key="article:author" property="article:author" content={validatedMetadata.author} />
          )}
          {publishedTime && (
            <meta key="article:published_time" property="article:published_time" content={publishedTime} />
          )}
          {modifiedTime && <meta key="article:modified_time" property="article:modified_time" content={modifiedTime} />}
          {section && <meta key="article:section" property="article:section" content={section} />}
          {tags.map((tag, index) => (
            <meta key={`article:tag:${index}`} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Video specific Open Graph tags */}
      {socialContentType === 'video.other' && videoUrl && (
        <>
          <meta key="og:video" property="og:video" content={videoUrl} />
          <meta key="og:video:type" property="og:video:type" content="text/html" />
          {videoWidth && <meta key="og:video:width" property="og:video:width" content={videoWidth.toString()} />}
          {videoHeight && <meta key="og:video:height" property="og:video:height" content={videoHeight.toString()} />}
          {videoDuration && <meta key="og:video:duration" property="og:video:duration" content={videoDuration} />}
        </>
      )}

      {/* Twitter Cards */}
      <meta key="twitter:card" name="twitter:card" content={twitterCard} />
      <meta key="twitter:title" name="twitter:title" content={socialTitle} />
      {resolvedTwitterDescription !== null ? (
        <meta key="twitter:description" name="twitter:description" content={resolvedTwitterDescription} />
      ) : null}
      <meta key="twitter:image" name="twitter:image" content={pageOgImage} />
      <meta
        key="twitter:image:alt"
        name="twitter:image:alt"
        content={`${cleanTitle} - ${validatedMetadata.description}`}
      />
      {branding.seoMeta.twitterHandle && (
        <meta key="twitter:site" name="twitter:site" content={branding.seoMeta.twitterHandle} />
      )}
      {branding.seoMeta.twitterHandle && (
        <meta key="twitter:creator" name="twitter:creator" content={branding.seoMeta.twitterHandle} />
      )}

      {/* Additional Twitter tags for better engagement */}
      {tags.length > 0 && (
        <>
          <meta key="twitter:label1" name="twitter:label1" content="Tags" />
          <meta key="twitter:data1" name="twitter:data1" content={tags.slice(0, 3).join(', ')} />
        </>
      )}

      {/* Canonical URL */}
      <link key="canonical" rel="canonical" href={pageCanonicalUrl} />

      {/* Additional meta tags for better SEO */}
      <meta key="theme-color" name="theme-color" content={branding.seoMeta.themeColor || '#3b82f6'} />
      <meta
        key="msapplication-TileColor"
        name="msapplication-TileColor"
        content={branding.seoMeta.themeColor || '#3b82f6'}
      />

      {/* Structured data hints for search engines */}
      <meta key="application-name" name="application-name" content={branding.appTitle} />
      <meta key="apple-mobile-web-app-title" name="apple-mobile-web-app-title" content={branding.appTitle} />
      <meta key="apple-mobile-web-app-capable" name="apple-mobile-web-app-capable" content="yes" />
      <meta
        key="apple-mobile-web-app-status-bar-style"
        name="apple-mobile-web-app-status-bar-style"
        content="default"
      />

      {/* Language and region */}
      <meta key="language" name="language" content={branding.seoMeta.language || 'Vietnamese'} />
      <meta key="geo.region" name="geo.region" content={branding.seoMeta.geoRegion || 'VN'} />
      <meta key="geo.placename" name="geo.placename" content={branding.seoMeta.geoPlacename || 'Vietnam'} />

      {/* Structured Data for Articles */}
      {socialContentType === 'article' && (
        <script
          key="structured-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: stringifyJsonLd({
              ...buildArticleStructuredData({
                cleanTitle,
                description: validatedMetadata.description,
                author: validatedMetadata.author,
                publisherName: branding.publisherName,
                publisherLogoUrl: branding.logoUrl,
                publishedTime,
                modifiedTime,
                pageOgImage,
                pageCanonicalUrl,
                hostname,
              }),
            }),
          }}
        />
      )}
    </Head>
  )
}

export default SEO
